<template>
  <PageLayout title="Tasks" class="!p-0">
    <div
      class="flex flex-col max-h-[100dvh] landscape:max-h-screen h-[100dvh] landscape:h-screen overflow-hidden"
    >
      <div :class="{ 'flex flex-grow bg-[#F2F4F7]': !showMobileVersion }">
        <div
          class="flex-col overflow-y-auto border-r border-gray-200 bg-white pt-2 pb-2 w-1/6 min-w-min hidden sm:flex"
        >
          <div
            class="flex items-center space-x-2 pt-4 pl-8 text-xl font-medium text-gray-900"
          >
            <ArrowLeft
              class="w-6 text-gray-500 hover:text-gray-900 hover:cursor-pointer"
              @click="handleBack"
            />
            <h1 class="truncate text-ellipsis w-40">{{ tenant.name }}</h1>
          </div>
          <div class="mt-5 flex flex-grow flex-col">
            <nav
              class="flex-1 space-y-8 bg-white px-4 sm:px-6 lg:px-8"
              aria-label="Sidebar"
            >
              <div class="space-y-1">
                <span
                  @click="handleClicked(item)"
                  v-for="item in settings.filter((s) => !s.hidden)"
                  :key="item.name"
                  :class="[
                    isActive(item.href)
                      ? 'bg-gray-50 text-gray-900 hover:text-gray-900 hover:bg-gray-100'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50',
                    'group flex items-center px-2 py-2 text-sm font-medium rounded-sm cursor-pointer',
                  ]"
                >
                  <component
                    :is="item.icon"
                    :class="[
                      isActive(item.href)
                        ? 'text-gray-900'
                        : 'text-gray-500 group-hover:text-gray-500',
                      'mr-3 flex-shrink-0 h-5 w-5',
                    ]"
                    aria-hidden="true"
                  />
                  <span class="truncate flex-1">{{ item.name }}</span>
                </span>
              </div>
            </nav>
          </div>
        </div>
        <div
          :class="{
            'flex flex-grow flex-col overflow-x-auto no-scrollbar':
              !showMobileVersion,
          }"
        >
          <slot />
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script setup>
import { usePage } from "@inertiajs/vue3";
import PageLayout from "@/Layouts/PageLayout.vue";
import route from "ziggy-js";
import TagOne from "@/Tailwind/Icon/TagOne";
import UsersOne from "@/Tailwind/Icon/UsersOne";
import ShieldTwo from "@/Tailwind/Icon/ShieldTwo";
import MessageSmileCircle from "@/Tailwind/Icon/MessageSmileCircle";
import ArrowLeft from "@/Tailwind/Icon/ArrowLeft";
import Information from "@/Tailwind/Icon/Information";
import { router } from "@inertiajs/vue3";
import { useBreakpoints } from "@/Tailwind/Mixins/useBreakpoints";
import { computed, inject } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { hasPermission, permissions } = inject("permissions");

const tenant = usePage().props.tenant;

const settings = [
  {
    name: t("admin.settings.location_settings.title"),
    icon: Information,
    href: route("settings.location.index", tenant),
    current: true,
    // Settings will never be hidden, only readonly/updateable
    hidden: false,
  },
  {
    name: t("admin.settings.categories.title"),
    icon: TagOne,
    href: route("settings.categories.index", tenant),
    current: false,
    hidden: !hasPermission(permissions.readCategory),
  },
  {
    name: t("admin.settings.users.title"),
    icon: UsersOne,
    href: route("settings.users.index", tenant),
    current: false,
    hidden: !hasPermission(permissions.readUser),
  },
  {
    name: t("admin.settings.permissions.title"),
    icon: ShieldTwo,
    href: route("settings.roles.index", tenant),
    current: false,
    hidden: !hasPermission(permissions.readRole),
  },
  {
    name: t("admin.settings.messenger.title"),
    icon: MessageSmileCircle,
    href: route("settings.messenger.index", tenant),
    current: false,
    hidden: !hasPermission(permissions.readMessengerFlow),
  },
];

const { type } = useBreakpoints();

function handleBack() {
  if (showMobileVersion.value) {
    router.visit(route("settings.organisation.index"));
    return;
  }

  router.visit(route("settings.organisation.buildings"));
}

const showMobileVersion = computed(() => {
  return type.value === "xs";
});

function handleClicked(item) {
  router.visit(item.href);
}

function isActive(to) {
  const url = `${location.origin}${location.pathname}`;

  return to === url;
}
</script>
